import { Card, Progress, Row, Col, Tag } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { CardSkeleton } from './CardSkeleton';
import { SHARED_CARD_STYLES } from '../constants';
import { SharedCardHeader } from './SharedCardHeader';
import { useApplicationsWorkaroundCompliance } from '../hooks/useApplicationsWorkaroundCompliance';

type ComplianceCardProps = {
  title: string;
  tooltipText: string;
  locationUUID?: string;
  showViewButton?: boolean;
  onViewClick?: () => void;
  linkTo?: string;
  columns?: {
    metric: string;
    applications: string;
    compliance: string;
  };
  highlightedMetricColor?: string;
  highlightedBackgroundColor?: string;
  disabled?: boolean;
};

export const ComplianceCard = ({
  title,
  tooltipText,
  showViewButton = true,
  locationUUID,
  onViewClick,
  linkTo,
  columns = {
    metric: 'Metric',
    applications: 'Compliance Percentage',
    compliance: '# of Applications',
  },
  highlightedMetricColor = '#3CB371',
  highlightedBackgroundColor = '#f0f8f0',
  disabled = false,
}: ComplianceCardProps) => {
  const navigate = useNavigate();

  const handleViewClick = () => {
    if (onViewClick) {
      onViewClick();
    } else if (linkTo) {
      navigate(linkTo);
    }
  };

  const shouldShowViewButton = Boolean(
    showViewButton && (onViewClick || linkTo) && !disabled,
  );

  const { data: metrics } = useApplicationsWorkaroundCompliance(locationUUID);

  return (
    <Card
      className={SHARED_CARD_STYLES.className}
      headStyle={SHARED_CARD_STYLES.headStyle}
      bodyStyle={SHARED_CARD_STYLES.bodyStyle}
      title={
        <SharedCardHeader
          title={title}
          tooltipText={tooltipText}
          disabled={disabled}
          shouldShowViewButton={shouldShowViewButton}
          onViewClick={handleViewClick}
        />
      }
      bordered={false}
    >
      {disabled ? (
        <CardSkeleton />
      ) : (
        <div className="w-full">
          <Row className="px-4 text-sm font-medium">
            <Col span={4} className="px-2">
              {columns.metric}
            </Col>
            <Col span={12} className="px-2">
              {columns.applications}
            </Col>
            <Col span={8} className="px-2">
              {columns.compliance}
            </Col>
          </Row>
          {metrics?.map((row, index) => (
            <Row
              key={row.metric}
              align="middle"
              className={`text-sm p-4 ${
                row.isHighlighted ? `bg-[${highlightedBackgroundColor}]` : ''
              } ${
                index === metrics.length - 1 ? '' : 'border-b border-gray-200'
              }`}
              style={{
                backgroundColor: row.isHighlighted
                  ? highlightedBackgroundColor
                  : undefined,
              }}
            >
              <Col span={4} className="px-2 relative">
                {row.isHighlighted && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '-1rem',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: '#fff',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      border: `4px solid ${highlightedMetricColor}`,
                    }}
                  >
                    <CheckCircleFilled
                      style={{
                        color: highlightedMetricColor,
                        fontSize: '20px',
                      }}
                    />
                  </div>
                )}
                <Tag
                  className={
                    row.isHighlighted
                      ? 'font-bold'
                      : 'bg-gray-100 font-bold text-black'
                  }
                  style={{
                    backgroundColor: row.isHighlighted
                      ? 'transparent'
                      : undefined,
                    color: row.isHighlighted
                      ? highlightedMetricColor
                      : undefined,
                    borderColor: row.isHighlighted
                      ? highlightedMetricColor
                      : undefined,
                  }}
                >
                  {row.metric}
                </Tag>
              </Col>
              <Col span={12} className="px-2">
                <Progress
                  percent={row.percentage}
                  strokeWidth={16}
                  showInfo={false}
                  strokeColor={row.color || '#2D5BFF'}
                  trailColor="#E6EFFF"
                  style={{
                    marginBottom: '0px',
                  }}
                />
              </Col>
              <Col span={8} className="px-2">
                <span
                  className={
                    row.isHighlighted ? 'font-medium' : 'text-gray-600'
                  }
                  style={{
                    color: row.isHighlighted
                      ? highlightedMetricColor
                      : undefined,
                  }}
                >
                  <span className="font-bold">{row.percentage}% </span>
                  <span className="text-gray-400">({row.count})</span>
                </span>
              </Col>
            </Row>
          ))}
        </div>
      )}
    </Card>
  );
};
